import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { useRef } from "react";
import PageHero from "@/components/PageHero";
import { FadeIn, ClipReveal, Eyebrow } from "@/components/Reveal";
import { useSiteContent } from "@/hooks/useSiteContent";
import { IMAGES, STRENGTHS, COMPANY } from "@/data/content";

const TIMELINE = [
  {
    year: "1958",
    title: "La fondazione",
    text: "Francesco Cavallo fonda l'azienda, piantando il primo palo di una storia che dura da oltre 60 anni.",
  },
  {
    year: "Gen.",
    title: "La seconda generazione",
    text: "I figli portano avanti la tradizione familiare, crescendo in competenze e cantieri.",
  },
  {
    year: "Oggi",
    title: "I nipoti",
    text: "Gli attuali nipoti guidano l'azienda unendo tradizione e innovazione, con gli stessi valori di sempre.",
  },
];

export default function ChiSiamoPage() {
  const { content } = useSiteContent();
  const collageRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: collageRef, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [90, -60]);
  const y3 = useTransform(scrollYProgress, [0, 1], [20, -20]);

  return (
    <>
      <PageHero
        eyebrow="Chi siamo"
        lines={["Tre generazioni", "di cantiere"]}
        description={`Fondata nel 1958 a Savoia di Lucania (PZ) da Francesco Cavallo, ${COMPANY.brand} è oggi una realtà solida che unisce tradizione e innovazione.`}
        image={IMAGES.scaffoldSteel}
      />

      <section className="py-24 lg:py-32" data-testid="chi-siamo-story">
        <div className="mx-auto grid w-full max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.5fr] lg:gap-24">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <Eyebrow>La nostra storia</Eyebrow>
            <FadeIn delay={0.05}>
              <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
                Un'azienda
                <span className="block text-brand-ice">di famiglia</span>
              </h2>
            </FadeIn>
          </div>
          <div className="space-y-10 text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="chi-siamo-paragraphs">
            <FadeIn>
              <p>{content.chi_siamo_p1}</p>
            </FadeIn>
            <FadeIn delay={0.08}>
              <p>{content.chi_siamo_p2}</p>
            </FadeIn>
            <FadeIn delay={0.16}>
              <p>{content.chi_siamo_p3}</p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-brand-teal py-24 lg:py-32" data-testid="chi-siamo-timeline">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
          <Eyebrow>1958 — Oggi</Eyebrow>
          <div className="mt-14 grid gap-10 md:grid-cols-3 md:gap-px md:bg-white/10 md:border md:border-white/10">
            {TIMELINE.map((t, i) => (
              <FadeIn key={t.year} delay={0.08 * i} amount={0.3}>
                <div className="h-full bg-brand-teal p-8 lg:p-10">
                  <p className="font-display text-5xl font-bold text-brand-ice sm:text-6xl">{t.year}</p>
                  <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight text-white">
                    {t.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400">{t.text}</p>
                  {i < 2 && (
                    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 md:hidden">
                      ↓
                    </p>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section ref={collageRef} className="overflow-hidden py-24 lg:py-32" data-testid="chi-siamo-collage">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
          <Eyebrow>I nostri cantieri</Eyebrow>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <motion.div style={{ y: y1 }} className="lg:mt-16">
              <ClipReveal>
                <img src={IMAGES.drillUrban} alt="Perforazioni in cantiere" className="aspect-[3/4] w-full object-cover" />
              </ClipReveal>
            </motion.div>
            <motion.div style={{ y: y2 }} className="lg:-mt-6">
              <ClipReveal delay={0.1}>
                <img src={IMAGES.scaffoldRest} alt="Restauro con impalcato" className="aspect-[3/4] w-full object-cover" />
              </ClipReveal>
            </motion.div>
            <motion.div style={{ y: y3 }} className="lg:mt-24">
              <ClipReveal delay={0.2}>
                <img src={IMAGES.ropeCliff} alt="Tecnico alpinista su parete" className="aspect-[3/4] w-full object-cover" />
              </ClipReveal>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#05090D] py-24 lg:py-32" data-testid="chi-siamo-strengths">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
          <Eyebrow>I nostri punti di forza</Eyebrow>
          <div className="mt-14">
            {STRENGTHS.map((s, i) => (
              <FadeIn key={s} delay={0.05 * i} amount={0.4}>
                <div
                  className="group flex items-center gap-6 border-t border-white/10 py-6 transition-colors last:border-b hover:bg-white/[0.03] sm:gap-10"
                  data-testid={`strength-item-${i + 1}`}
                >
                  <span className="font-mono text-xs text-brand-cyan">0{i + 1}</span>
                  <Check className="h-5 w-5 shrink-0 text-brand-ice" />
                  <p className="font-display text-xl font-semibold uppercase tracking-tight text-white transition-colors group-hover:text-brand-ice sm:text-3xl">
                    {s}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
